import co from 'co'
import _ from 'underscore'
import axios from 'axios'
import ApiError from './ApiError'
import repos from './Repository'
import Constants from '../constants'
import { setupCache } from 'axios-cache-adapter'

function _res (promise) {
  return promise
    .then(response => {
      return Promise.resolve(response.data)
    })
    .catch(error => {
      if (axios.isCancel(error)) {
        return Promise.resolve()
      }
      if (error.response) {
        let data = null
        if (error.response && error.response.data) {
          data = error.response.data
        }
        let status = -999
        if (error.response && error.response.status) {
          status = error.response.status
        }
        if (status === 401) {
          repos.setToken(null)
        }
        return Promise.reject(new ApiError('api error', status, data))
      } else {
        if (error.code === 'ECONNABORTED') {
          // timeout
          return Promise.reject(new ApiError('api error', -1, null))
        }
        // network error
        return Promise.reject(new ApiError('api error', -2, null))
      }
    })
}

class Api {
  _req (func) {
    const _this = this
    return co(function * () {
      yield _this.refreshToken()
      return yield func()
    })
  }

  initialize () {
    const cache = setupCache({
      maxAge: 1.5 * 1000
    })

    /** @private **/
    this.client = axios.create({
      adapter: cache.adapter,
      baseURL: process.env.VUE_APP_API_ENDPOINT,
      timeout: process.env.VUE_APP_API_TIMEOUT,
      responseType: 'json',
      headers: {
        'Accept': 'application/json'
      }
    })

    this.client.interceptors.request.use(config => {
      try {
        config.headers['X-Requested-With'] = 'XMLHttpRequest'
        const token = repos.getToken()
        if (token && token.access_token) {
          config.headers['Authorization'] = `Bearer ${token.access_token}`
        }
      } catch (error) {
        console.error(error)
      }
      return config
    })
  }

  login (subDomain, loginId, password) {
    var params = new URLSearchParams()
    params.append('sub_domain', subDomain)
    params.append('login_id', loginId)
    params.append('password', password)
    console.info(subDomain)
    const path = '/api/auth/login'
    return _res(this.client.post(path, params, { timeout: 180000 }))
      .then(res => {
        repos.setToken(res)
      })
  }

  logout () {
    const path = '/api/auth/logout'
    return _res(this.client.delete(path))
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        repos.setToken(null)
        console.info('logout')
      })
  }

  profile () {
    const path = '/api/profile'
    return _res(this.client.get(path))
  }

  refreshToken () {
    var token = repos.getToken()
    if (!token) return Promise.resolve()

    const saveTime = token.saveDate
    if (saveTime + (token.expires_in * 1000 / 2) < new Date().getTime()) {
      const path = '/api/auth/refresh'
      return _res(this.client.put(path, []))
        .then(res => {
          repos.setToken(res)
        })
    }
    return Promise.resolve()
  }

  // ========================
  //  Code
  // ========================
  getServiceCode () {
    const path = `/api/service_code`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  getTeresaStandardServiceCode () {
    const path = `/api/service_teresa_code`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  getCode () {
    const path = `/api/code`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  getCheckUniqueCode (serviceUniqueCodeId) {
    const path = `/api/code/check/${serviceUniqueCodeId}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  postUniqueCode (values) {
    console.info(values)
    const path = `/api/unique_code`
    return this._req(() => {
      return _res(this.client.post(path, values))
    })
  }

  postTeresaStandardUniqueCode (values) {
    console.info(values)
    const path = `/api/teresa_unique_code`
    return this._req(() => {
      return _res(this.client.post(path, values))
    })
  }

  // ========================
  //  Office
  // ========================
  getOffice (mode = Constants.QUERY_MODE_PAGING) {
    const path = `/api/office?mode=${mode}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  // ========================
  //  User
  // ========================
  getUser (mode = Constants.QUERY_MODE_PAGING, page, officeId, userDisplayName, userDisplayNameKana, inChargeFlag = '', sort = '') {
    officeId = (officeId) || ''
    userDisplayName = (userDisplayName) || ''
    userDisplayNameKana = (userDisplayNameKana) || ''
    inChargeFlag = (inChargeFlag) || ''
    const path = `/api/user?mode=${mode}&page=${page}&office_id=${officeId}&user_display_name=${userDisplayName}&user_display_name_kana=${userDisplayNameKana}&in_charge_flag=${inChargeFlag}&sort=${sort}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  findUser (userId) {
    const path = `/api/user/${userId}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  postUser (values) {
    const path = `/api/user`
    return this._req(() => {
      return _res(this.client.post(path, values))
    })
  }

  putUser (userId, values) {
    const path = `/api/user/${userId}`
    return this._req(() => {
      return _res(this.client.put(path, values))
    })
  }

  deleteUser (userId) {
    const path = `/api/user/${userId}`
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  exportUser (officeId) {
    const path = '/api/export/user' + toQueryString(officeId)
    return this._req(() => {
      return this.client.get(path, {
        responseType: 'blob',
        timeout: 180000
      })
    })
  }

  exportTeresaUser (officeId) {
    const path = '/api/export_teresa/user' + toQueryString(officeId)
    return this._req(() => {
      return this.client.get(path, {
        responseType: 'blob',
        timeout: 180000
      })
    })
  }

  // ========================
  //  InstructionTemplate
  // ========================
  getInstructionTemplate (mode = Constants.QUERY_MODE_PAGING, page) {
    const path = `/api/instruction_template?mode=${mode}&page=${page}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  findInstructionTemplate (id) {
    const path = `/api/instruction_template/${id}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  postInstructionTemplate (values) {
    const path = `/api/instruction_template`
    return this._req(() => {
      return _res(this.client.post(path, values))
    })
  }

  putInstructionTemplate (id, values) {
    const path = `/api/instruction_template/${id}`
    return this._req(() => {
      return _res(this.client.put(path, values))
    })
  }

  deleteInstructionTemplate (id) {
    const path = `/api/instruction_template/${id}`
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  // ========================
  //  CustomServiceCode
  // ========================
  getCustomServiceCode (mode = Constants.QUERY_MODE_PAGING, page) {
    const path = `/api/custom_service_code?mode=${mode}&page=${page}`
    console.info(path)
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  findCustomServiceCode (id) {
    const path = `/api/custom_service_code/${id}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  postCustomServiceCode (values) {
    const path = `/api/custom_service_code`
    return this._req(() => {
      return _res(this.client.post(path, values))
    })
  }

  putCustomServiceCode (id, values) {
    const path = `/api/custom_service_code/${id}`
    return this._req(() => {
      return _res(this.client.put(path, values))
    })
  }

  deleteCustomServiceCode (id) {
    const path = `/api/custom_service_code/${id}`
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  // ========================
  //  Staff
  // ========================
  getStaffs (mode = Constants.QUERY_MODE_PAGING, page, officeId, staffDisplayName, staffDisplayNameKana, sort = '') {
    officeId = (officeId) || ''
    staffDisplayName = (staffDisplayName) || ''
    staffDisplayNameKana = (staffDisplayNameKana) || ''
    const path = `/api/staff?mode=${mode}&page=${page}&office_id=${officeId}&staff_display_name=${staffDisplayName}&staff_display_name_kana=${staffDisplayNameKana}&sort=${sort}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  findStaff (userId) {
    const path = `/api/staff/${userId}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  postStaff (values) {
    const path = `/api/staff`
    return this._req(() => {
      return _res(this.client.post(path, values, { timeout: process.env.VUE_APP_API_TIMEOUT }))
    })
  }

  putStaff (userId, values) {
    const path = `/api/staff/${userId}`
    return this._req(() => {
      return _res(this.client.put(path, values))
    })
  }

  deleteStaff (userId) {
    const path = `/api/staff/${userId}`
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  exportStaff (officeId) {
    const path = '/api/export/staff' + toQueryString(officeId)
    return this._req(() => {
      return this.client.get(path, {
        responseType: 'blob',
        timeout: 180000
      })
    })
  }

  exportTeresaStandardStaff (officeId) {
    const path = '/api/export/teresa-standard/staff' + toQueryString(officeId)
    return this._req(() => {
      return this.client.get(path, {
        responseType: 'blob',
        timeout: 180000
      })
    })
  }

  // ========================
  //  Plan
  // ========================
  getPlans (args) {
    const path = '/api/plan' + toQueryString(args)
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  exportPlans (args) {
    const path = '/api/export/plan' + toQueryString(args)
    return this._req(() => {
      return this.client.get(path, {
        responseType: 'blob',
        timeout: 180000
      })
    })
  }

  exportTeresaStandardPlans (args) {
    const path = '/api/export/teresa-standard/plan' + toQueryString(args)
    return this._req(() => {
      return this.client.get(path, {
        responseType: 'blob',
        timeout: 180000
      })
    })
  }

  exportF1Plans (args) {
    const path = '/api/export/f1/plan' + toQueryString(args)
    return this._req(() => {
      return _res(this.client.get(path, {
        responseType: 'blob',
        timeout: 180000
      }))
    })
  }

  deletePlan (id) {
    const path = `/api/plan/${id}`
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  deletePlans (ids) {
    const params = {
      plan_ids: ids
    }
    console.info(params)
    const path = '/api/delete/plans' + toQueryString(params)
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  getPlan (id) {
    const path = `/api/plan/${id}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  createPlan (args) {
    const path = '/api/plan'
    return this._req(() => {
      return _res(this.client.post(path, args))
    })
  }

  updatePlan (args) {
    const path = `/api/plan/${args.id}`
    return this._req(() => {
      return _res(this.client.put(path, args))
    })
  }

  updateBulkPlan (args) {
    const path = `/api/plans/update-instruction-bulk`
    return this._req(() => {
      return _res(this.client.put(path, args))
    })
  }

  copyOnePlan (args) {
    const path = `/api/plan/${args.id}/copy`
    return this._req(() => {
      return _res(this.client.post(path, args))
    })
  }

  copyPlan (officeId, userId, srcStartOfWeek, dst, planCopyType) {
    const params = {
      office_id: officeId,
      user_id: userId,
      src_start_of_week: srcStartOfWeek,
      dst: dst,
      plan_copy_type: planCopyType
    }
    const path = '/api/copy/plan'
    return this._req(() => {
      return _res(this.client.post(path, params))
    })
  }

  copyPlans (officeId, userId, srcStartOfWeek, dsts, planCopyType) {
    const params = {
      office_id: officeId,
      user_id: userId,
      src_start_of_week: srcStartOfWeek,
      dsts: dsts,
      plan_copy_type: planCopyType
    }
    console.info(params)
    const path = '/api/copy/plans'
    return this._req(() => {
      return _res(this.client.post(path, params))
    })
  }

  copyShiftWeek (args) {
    const path = '/api/copy/shift-week' + toQueryString(args)
    return this._req(() => {
      return _res(this.client.post(path))
    })
  }

  copyShiftDay (args) {
    const path = '/api/copy/shift-day' + toQueryString(args)
    return this._req(() => {
      return _res(this.client.post(path))
    })
  }

  deleteRangePlan (officeId, userId, fromStartDay, toStartDay) {
    const params = {
      office_id: officeId,
      user_id: userId,
      from_start_day: fromStartDay,
      to_start_day: toStartDay
    }
    console.info(params)
    const path = '/api/delete/plan' + toQueryString(params)
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  // ========================
  //  Report
  // ========================
  createReport (args, ignoreDelete) {
    ignoreDelete = (ignoreDelete) || ''
    const path = `/api/report?ignore_delete=${ignoreDelete}`
    console.info(path)
    return this._req(() => {
      return _res(this.client.post(path, args))
    })
  }

  updateReport (args) {
    const path = `/api/report/${args.id}`
    console.info(path)
    return this._req(() => {
      return _res(this.client.put(path, args))
    })
  }

  deleteReport (id) {
    const path = `/api/report/${id}`
    console.info(path)
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  getReportPdfUrl (id) {
    const path = `/api/report/${id}/pdf`
    console.info(path)
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  // ========================
  //  import
  // ========================
  // eslint-disable-next-line camelcase
  importF1User ({ office_id, file }) {
    let formData = new FormData()
    formData.append('office_id', office_id)
    formData.append('file', file)
    let config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const path = `/api/import/f1/user`
    console.info(path)
    return this._req(() => {
      return _res(this.client.post(path, formData, config))
    })
  }

  // eslint-disable-next-line camelcase
  importKaigoNoMoriUser ({ office_id, file }) {
    let formData = new FormData()
    formData.append('office_id', office_id)
    formData.append('file', file)
    let config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const path = `/api/import/kaigo-no-mori/user`
    console.info(path)
    return this._req(() => {
      return _res(this.client.post(path, formData, config))
    })
  }

  // eslint-disable-next-line camelcase
  importTeresaStandardUser ({ office_id, file }) {
    let formData = new FormData()
    formData.append('office_id', office_id)
    formData.append('file', file)
    let config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const path = `/api/import/teresa_standard/user`
    return this._req(() => {
      return _res(this.client.post(path, formData, config))
    })
  }

  // eslint-disable-next-line camelcase
  importKaigoNoMoriStaff ({ office_id, file }) {
    let formData = new FormData()
    formData.append('office_id', office_id)
    formData.append('file', file)
    let config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const path = `/api/import/kaigo-no-mori/staff`
    console.info(path)
    return this._req(() => {
      return _res(this.client.post(path, formData, config))
    })
  }

  // eslint-disable-next-line camelcase
  importTeresaStandardStaff ({ office_id, file }) {
    let formData = new FormData()
    formData.append('office_id', office_id)
    formData.append('file', file)
    let config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const path = `/api/import/teresa-standard/staff`
    return this._req(() => {
      return _res(this.client.post(path, formData, config))
    })
  }

  // eslint-disable-next-line camelcase
  importKaigoNoMoriManager ({ office_id, file }) {
    let formData = new FormData()
    formData.append('office_id', office_id)
    formData.append('file', file)
    let config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const path = `/api/import/kaigo-no-mori/manager`
    console.info(path)
    return this._req(() => {
      return _res(this.client.post(path, formData, config))
    })
  }

  // eslint-disable-next-line camelcase
  importKaigoNoMoriSchedule ({ office_id, file }) {
    let formData = new FormData()
    formData.append('office_id', office_id)
    formData.append('file', file)
    let config = {
      headers: {
        'content-type': 'multipart/form-data'
      },
      timeout: 180000
    }
    const path = `/api/import/kaigo-no-mori/schedule`
    console.info(path)
    return this._req(() => {
      return _res(this.client.post(path, formData, config))
    })
  }

  exportKaigoNoMoriReports (args) {
    const path = '/api/export/kaigo-no-mori/report' + toQueryString(args)
    return this._req(() => {
      return _res(this.client.get(path, {
        responseType: 'blob',
        timeout: 180000
      }))
    })
  }

  // eslint-disable-next-line camelcase
  importTeresaStandardSchedule ({ office_id, file }) {
    let formData = new FormData()
    formData.append('office_id', office_id)
    formData.append('file', file)
    let config = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    const path = `/api/import/teresa-standard/schedule`
    return this._req(() => {
      return _res(this.client.post(path, formData, config))
    })
  }

  // ========================
  //  WorkOrder
  // ========================
  getWorkOrder (mode = Constants.QUERY_MODE_PAGING, page = 1) {
    const path = `/api/work_order?mode=${mode}&page=${page}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  findWorkOrder (id) {
    const path = `/api/work_order/${id}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  postWorkOrder (values) {
    const path = `/api/work_order`
    return this._req(() => {
      return _res(this.client.post(path, values))
    })
  }

  putWorkOrder (id, values) {
    const path = `/api/work_order/${id}`
    return this._req(() => {
      return _res(this.client.put(path, values))
    })
  }

  deleteWorkOrder (id) {
    const path = `/api/work_order/${id}`
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }

  // ========================
  //  PlanTemplate
  // ========================
  getPlanTemplate (mode = Constants.QUERY_MODE_PAGING, page) {
    const path = `/api/plan_template?mode=${mode}&page=${page}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  findPlanTemplate (id) {
    const path = `/api/plan_template/${id}`
    return this._req(() => {
      return _res(this.client.get(path))
    })
  }

  postPlanTemplate (values) {
    const path = `/api/plan_template`
    return this._req(() => {
      return _res(this.client.post(path, values))
    })
  }

  putPlanTemplate (id, values) {
    const path = `/api/plan_template/${id}`
    return this._req(() => {
      return _res(this.client.put(path, values))
    })
  }

  deletePlanTemplate (id) {
    const path = `/api/plan_template/${id}`
    return this._req(() => {
      return _res(this.client.delete(path))
    })
  }
}

function toQueryString (params) {
  let result = ''
  _.each(params, (val, key) => {
    result += (result.length > 0) ? '&' : '?'
    result += (val) ? `${key}=${val}` : `${key}=`
  })
  return result
}

export default new Api()
